// src/views/AuditoriaTurnos.js
import React, { useState } from 'react';
import axiosInstance from '../config/axiosConfig';

/* ─── helpers de fecha ────────────────────────────────────────── */

const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// Período: Si el día de hoy es > 25, el periodo es del 26 de este mes al 25 del próximo.
// Si el día es <= 25, es del 26 del mes pasado al 25 de este mes.
const inicioPeriodo = () => {
    const d = new Date();
    if (d.getDate() > 25) {
        return toYMD(new Date(d.getFullYear(), d.getMonth(), 26));
    }
    return toYMD(new Date(d.getFullYear(), d.getMonth() - 1, 26));
};

const finPeriodo = () => {
    const d = new Date();
    if (d.getDate() > 25) {
        return toYMD(new Date(d.getFullYear(), d.getMonth() + 1, 25));
    }
    return toYMD(new Date(d.getFullYear(), d.getMonth(), 25));
};

const formatFecha = (str) => {
    if (!str) return '—';
    const [fecha, hora] = str.split(' ');
    if (!fecha) return str;
    const [y, m, dd] = fecha.split('-');
    return `${dd}-${m}-${y}${hora ? ' ' + hora.slice(0, 5) : ''}`;
};

const formatFechaTurno = (str) => {
    if (!str) return '—';
    const [y, m, dd] = str.split('-');
    return `${dd}-${m}-${y}`;
};

/* ─── helpers para renderizar valores genéricos ───────────────── */

/**
 * Convierte cualquier valor (primitivo, objeto, array) en algo
 * renderizable de forma legible. Sin [object Object].
 */
const valorLegible = (v) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? 'Sí' : 'No';
    if (typeof v === 'number') return v.toLocaleString('es-CL');
    if (typeof v === 'string') return v || '—';
    // Objeto o array → JSON con indentación
    return JSON.stringify(v, null, 2);
};

const esComplejo = (v) =>
    v !== null && typeof v === 'object';

/* ─── constantes ──────────────────────────────────────────────── */

const LABELS_KEYS = {
    auxiliar_reemplazado: 'Auxiliar reemplazado',
    auxiliar_anterior : 'Auxiliar anterior',
    valor_turno       : 'Valor del turno',
    nota              : 'Nota',
    observacion       : 'Observación',
    antes             : 'Antes',
    despues           : 'Después',
    motivo            : 'Motivo',
    usuario           : 'Usuario',
    dias_solicitados  : 'Días solicitados',
    solicitud_id      : 'ID Solicitud',
    dias_reemplazados : 'Días reemplazados',
    auxiliares_asignados: 'Auxiliares Asignados',
    turnos_exitosos   : 'Turnos actualizados',
    dias              : 'Días'
};

const labelKey = (k) => LABELS_KEYS[k] || k.replace(/_/g, ' ');

const TIPOS_CAMBIO = [
    { id: '', label: 'Todos los tipos' },
    { id: '1', label: 'Asignación de turno' },
    { id: '2', label: 'Eliminación de turno' },
    { id: '3', label: 'Reemplazo de auxiliar' },
    { id: '4', label: 'Cambio de periodo' },
    { id: '5', label: 'Modificación de valor' },
    { id: '6', label: 'Nota/Observación' },
    { id: '7', label: 'Otro' },
    { id: '8', label: 'Creación de acuerdo mutuo' },
    { id: '9', label: 'Cierre de acuerdo mutuo' },
    { id: '10', label: 'Turno de apoyo' },
    { id: '11', label: 'Crear Solicitud Reemplazo' },
    { id: '12', label: 'Resolver Solicitud Reemplazo' },
    { id: '13', label: 'Eliminar Solicitud Reemplazo' },
    { id: '14', label: 'Crear Vacaciones' },
    { id: '15', label: 'Eliminar Vacaciones' },
    { id: '16', label: 'Modificación de rotación' },
];

const BADGE_COLORS = {
    1: 'success',
    2: 'danger',
    3: 'warning',
    4: 'info',
    5: 'primary',
    6: 'secondary',
    7: 'dark',
    8: 'success',
    9: 'danger',
    10: 'info',
    11: 'primary',
    12: 'success',
    13: 'danger',
    14: 'warning',
    15: 'dark',
    16: 'info',
};

/* ─── subcomponente: renderiza un valor dentro del modal ─────── */
function ValorDetalle({ value }) {
    if (!esComplejo(value)) {
        return <span className="fw-semibold">{valorLegible(value)}</span>;
    }

    // Es objeto o array → tabla interna
    const entries = Array.isArray(value)
        ? value.map((v, i) => [String(i), v])
        : Object.entries(value);

    if (entries.length === 0) return <span className="text-muted">vacío</span>;

    return (
        <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
            <tbody>
                {entries.map(([k, v]) => (
                    <tr key={k}>
                        <td className="text-muted fw-semibold text-nowrap" style={{ width: 160 }}>
                            {labelKey(k)}
                        </td>
                        <td>
                            {esComplejo(v)
                                ? <pre className="mb-0 small text-body" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify(v, null, 2)}
                                  </pre>
                                : <span className="fw-semibold">{valorLegible(v)}</span>
                            }
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/* ─── modal de detalle ────────────────────────────────────────── */
function ModalDetalle({ registro, onClose }) {
    if (!registro) return null;
    const { datos_extra, descripcion, tipo_nombre, tipo_cambio,
            paciente_nombre, auxiliar_nombre, fecha_turno,
            periodo_label, usuario_nombre, fecha_registro } = registro;

    const entries = datos_extra ? Object.entries(datos_extra) : [];

    return (
        <>
            {/* Backdrop */}
            <div
                className="modal-backdrop fade show"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                onClick={onClose}
            />
            {/* Modal */}
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 1056 }}
            >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content border-0 shadow-lg">

                        {/* Header */}
                        <div className="modal-header"
                            style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-journal-text text-white fs-5" />
                                <div>
                                    <h6 className="modal-title text-white fw-bold mb-0">
                                        Detalle del registro #{registro.id}
                                    </h6>
                                    <small className="text-white-50">{formatFecha(fecha_registro)}</small>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                onClick={onClose}
                            />
                        </div>

                        <div className="modal-body p-4">

                            {/* Resumen rápido */}
                            <div className="row g-2 mb-4">
                                <div className="col-12">
                                    <div className="p-3 rounded-3 border bg-light">
                                        <div className="d-flex align-items-start gap-2">
                                            <i className="bi bi-info-circle text-primary mt-1" />
                                            <div>
                                                <div className="small text-muted mb-1">Descripción</div>
                                                <div className="fw-semibold">{descripcion}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <div className="p-3 rounded-3 border h-100">
                                        <div className="small text-muted mb-1">Tipo de cambio</div>
                                        <span className={`badge bg-${BADGE_COLORS[tipo_cambio] || 'secondary'}`}>
                                            {tipo_nombre}
                                        </span>
                                    </div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <div className="p-3 rounded-3 border h-100">
                                        <div className="small text-muted mb-1">Período turno</div>
                                        <span className={`badge ${registro.periodo_turno === 'night' ? 'bg-dark' : 'bg-warning text-dark'}`}>
                                            {periodo_label}
                                        </span>
                                        <div className="small fw-semibold mt-1">{formatFechaTurno(fecha_turno)}</div>
                                    </div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <div className="p-3 rounded-3 border h-100">
                                        <div className="small text-muted mb-1">Paciente</div>
                                        <div className="fw-semibold small">{paciente_nombre || '—'}</div>
                                    </div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <div className="p-3 rounded-3 border h-100">
                                        <div className="small text-muted mb-1">Auxiliar</div>
                                        <div className="fw-semibold small">{auxiliar_nombre || '—'}</div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="p-3 rounded-3 border">
                                        <div className="small text-muted mb-1">Realizado por</div>
                                        <div className="fw-semibold small">
                                            <i className="bi bi-person-check me-1 text-success" />
                                            {usuario_nombre || `ID ${registro.realizado_por}`}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Datos extra */}
                            {entries.length > 0 && (
                                <>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-code-square text-primary" />
                                        <h6 className="mb-0 fw-bold text-secondary">Datos adicionales</h6>
                                    </div>

                                    <div className="d-flex flex-column gap-3">
                                        {entries.map(([k, v]) => (
                                            <div key={k} className="border rounded-3 overflow-hidden">
                                                {/* Cabecera de la clave */}
                                                <div
                                                    className="px-3 py-2 fw-semibold small"
                                                    style={{
                                                        background: k === 'despues' ? '#e8f5e9'
                                                                  : k === 'antes'   ? '#fff3e0'
                                                                  : '#f0f4ff',
                                                        color: k === 'despues' ? '#2e7d32'
                                                             : k === 'antes'   ? '#e65100'
                                                             : '#1a237e',
                                                        borderBottom: '1px solid #dee2e6',
                                                    }}
                                                >
                                                    {k === 'antes'   && <i className="bi bi-arrow-left-circle me-2" />}
                                                    {k === 'despues' && <i className="bi bi-arrow-right-circle me-2" />}
                                                    {!(k === 'antes' || k === 'despues') && <i className="bi bi-dot me-1" />}
                                                    {labelKey(k)}
                                                </div>
                                                {/* Valor */}
                                                <div className="p-3">
                                                    <ValorDetalle value={v} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {entries.length === 0 && (
                                <div className="text-center py-3 text-muted">
                                    <i className="bi bi-slash-circle me-2" />
                                    Sin datos adicionales para este registro.
                                </div>
                            )}
                        </div>

                        <div className="modal-footer border-top-0">
                            <button className="btn btn-secondary" onClick={onClose}>
                                <i className="bi bi-x-lg me-1" />Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ─── componente principal ─────────────────────────────────────── */

function AuditoriaTurnos() {
    const [desde, setDesde] = useState(inicioPeriodo());
    const [hasta, setHasta] = useState(finPeriodo());
    const [tipoCambio, setTipoCambio] = useState('');
    const [paciente, setPaciente] = useState('');

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);
    const [error, setError] = useState('');

    // Modal
    const [registroDetalle, setRegistroDetalle] = useState(null);

    /* ─ búsqueda ─────────────────────────────── */
    const buscar = async () => {
        if (!desde || !hasta) { setError('Debe ingresar ambas fechas.'); return; }
        if (desde > hasta)    { setError('La fecha "Desde" no puede ser mayor a "Hasta".'); return; }
        setError('');
        setLoading(true);
        setBuscado(false);
        try {
            const params = { desde, hasta };
            if (tipoCambio)       params.tipo_cambio = tipoCambio;
            if (paciente.trim())  params.paciente    = paciente.trim();

            const resp = await axiosInstance.get('/auditoria-turnos/lista', { params });
            const body = resp.data;
            if (body.error) {
                setError(body.message || 'Error al obtener la auditoría.');
                setData([]); setTotal(null);
            } else {
                setData(body.data || []);
                setTotal(body.total ?? (body.data?.length ?? 0));
            }
        } catch (err) {
            console.error(err);
            setError('Error al conectar con el servidor.');
            setData([]); setTotal(null);
        } finally {
            setLoading(false);
            setBuscado(true);
        }
    };

    const limpiar = () => {
        setDesde(inicioPeriodo()); setHasta(finPeriodo());
        setTipoCambio(''); setPaciente('');
        setData([]); setTotal(null);
        setBuscado(false); setError('');
    };

    /* ─ render ───────────────────────────────── */
    return (
        <div className="container-fluid py-4 px-4">

            {/* ENCABEZADO */}
            <div className="d-flex align-items-center gap-2 mb-4">
                <i className="bi bi-journal-text fs-3 text-primary" />
                <div>
                    <h4 className="mb-0 fw-bold">Historial de Turnos</h4>
                    <small className="text-muted">Registro de cambios realizados en los turnos del sistema</small>
                </div>
            </div>

            {/* FILTROS */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-bottom py-3">
                    <h6 className="mb-0 fw-semibold text-secondary">
                        <i className="bi bi-funnel me-2" />Filtros de búsqueda
                    </h6>
                </div>
                <div className="card-body">
                    <div className="row g-3 align-items-end">

                        <div className="col-12 col-sm-6 col-md-3">
                            <label className="form-label fw-semibold small">
                                <i className="bi bi-calendar-event me-1 text-primary" />Desde
                            </label>
                            <input type="date" className="form-control" value={desde}
                                onChange={e => setDesde(e.target.value)} />
                        </div>

                        <div className="col-12 col-sm-6 col-md-3">
                            <label className="form-label fw-semibold small">
                                <i className="bi bi-calendar-event me-1 text-primary" />Hasta
                            </label>
                            <input type="date" className="form-control" value={hasta}
                                onChange={e => setHasta(e.target.value)} />
                        </div>

                        <div className="col-12 col-sm-6 col-md-2">
                            <label className="form-label fw-semibold small">
                                <i className="bi bi-tag me-1 text-primary" />Tipo de cambio
                            </label>
                            <select className="form-select" value={tipoCambio}
                                onChange={e => setTipoCambio(e.target.value)}>
                                {TIPOS_CAMBIO.map(t => (
                                    <option key={t.id} value={t.id}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-12 col-sm-6 col-md-2">
                            <label className="form-label fw-semibold small">
                                <i className="bi bi-person me-1 text-primary" />Paciente
                            </label>
                            <input type="text" className="form-control"
                                placeholder="Nombre o apellido…" value={paciente}
                                onChange={e => setPaciente(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && buscar()} />
                        </div>

                        <div className="col-12 col-md-2 d-flex gap-2">
                            <button className="btn btn-primary flex-grow-1" onClick={buscar} disabled={loading}>
                                {loading
                                    ? <><span className="spinner-border spinner-border-sm me-1" />Buscando…</>
                                    : <><i className="bi bi-search me-1" />Buscar</>
                                }
                            </button>
                            <button className="btn btn-outline-secondary" onClick={limpiar} title="Limpiar filtros">
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger mt-3 mb-0 py-2">
                            <i className="bi bi-exclamation-triangle me-2" />{error}
                        </div>
                    )}
                </div>
            </div>

            {/* RESULTADOS */}
            {buscado && (
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 fw-semibold text-secondary">
                            <i className="bi bi-table me-2" />Resultados
                        </h6>
                        {total !== null && (
                            <span className="badge bg-primary rounded-pill">
                                {total} registro{total !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>

                    <div className="card-body p-0">
                        {data.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="bi bi-inbox fs-1 d-block mb-2" />
                                No se encontraron registros para los filtros aplicados.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="ps-3">#</th>
                                            <th>Tipo</th>
                                            <th>Descripción</th>
                                            <th>Paciente</th>
                                            <th>Auxiliar</th>
                                            <th>Fecha turno</th>
                                            <th>Periodo</th>
                                            <th>Realizado por</th>
                                            <th>Fecha registro</th>
                                            <th className="text-center">Detalle</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((row) => (
                                            <tr key={row.id}>
                                                <td className="ps-3 text-muted small">{row.id}</td>

                                                <td>
                                                    <span className={`badge bg-${BADGE_COLORS[row.tipo_cambio] || 'secondary'}`}>
                                                        {row.tipo_nombre}
                                                    </span>
                                                </td>

                                                <td style={{ minWidth: 250, maxWidth: 350 }}>
                                                    <span className="small">{row.descripcion}</span>
                                                </td>

                                                <td className="small">{row.paciente_nombre || '—'}</td>
                                                <td className="small">{row.auxiliar_nombre  || '—'}</td>
                                                <td className="small text-nowrap">{formatFechaTurno(row.fecha_turno)}</td>

                                                <td>
                                                    <span className={`badge ${row.periodo_turno === 'night' ? 'bg-dark' : 'bg-warning text-dark'}`}>
                                                        {row.periodo_label}
                                                    </span>
                                                </td>

                                                <td className="small">{row.usuario_nombre || `ID ${row.realizado_por}`}</td>
                                                <td className="small text-nowrap">{formatFecha(row.fecha_registro)}</td>

                                                {/* Botón ver detalle */}
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        title="Ver detalle"
                                                        onClick={() => setRegistroDetalle(row)}
                                                    >
                                                        <i className="bi bi-eye me-1" />Ver
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* MODAL */}
            <ModalDetalle
                registro={registroDetalle}
                onClose={() => setRegistroDetalle(null)}
            />
        </div>
    );
}

export default AuditoriaTurnos;
